import { useStorage, toReactive } from '@vueuse/core'

/** 应用全局参数 */
export interface AppParams {
  token: string | null
  inset_top: number
  inset_bottom: number
  language: string | null
  client_id: string | null
  client_type: string | null
  version: string | null
  time_zone: string | null
  user_info: Record<string, any> | null
}

const DEFAULT_PARAMS: AppParams = {
  token: null,
  inset_top: 0,
  inset_bottom: 0,
  language: null,
  client_id: null,
  client_type: null,
  version: null,
  time_zone: null,
  user_info: null,
}

// useStorage 在模块顶层建立应用级单例：整个参数对象以 JSON 持久化到一个 localStorage key
const store = useStorage<AppParams>('app_params', DEFAULT_PARAMS)
// toReactive 把 Ref 转为「属性级」响应式对象，对外保持 params.token 的对象语义与响应性
const state = toReactive(store) as AppParams

/** 获取当前所有参数（同步，适合 service/bridge 等非组件代码） */
export function getAppParams(): AppParams {
  return state
}

/** 更新参数（写入内存 + localStorage；useStorage 深度监听嵌套修改并自动持久化） */
export function setAppParams(partial: Partial<AppParams>): void {
  Object.assign(store.value, partial)
}

/** URL query 参数元数据：旧版 App 通过 URL query 传参，声明每个字段的 query 名与解析方式 */
interface QueryParamMeta {
  /** URL query 参数名 */
  urlKey: string
  /** 解析方式：'json' → JSON.parse；'number' → Number()（>0 才采用）；'string' → 原样字符串 */
  serialize: 'json' | 'string' | 'number'
}

const QUERY_META: Record<keyof AppParams, QueryParamMeta> = {
  token: { urlKey: 'token', serialize: 'string' },
  inset_top: { urlKey: 'inset_top', serialize: 'number' },
  inset_bottom: { urlKey: 'inset_bottom', serialize: 'number' },
  language: { urlKey: 'language', serialize: 'string' },
  client_id: { urlKey: 'client_id', serialize: 'string' },
  client_type: { urlKey: 'client_type', serialize: 'string' },
  version: { urlKey: 'version', serialize: 'string' },
  time_zone: { urlKey: 'time_zone', serialize: 'string' },
  user_info: { urlKey: 'user_info', serialize: 'json' },
}

// inset 兜底默认值
const INSET_TOP_DEFAULT = 59
const INSET_BOTTOM_DEFAULT = 34

/** 从 URL query 解析旧版传参（仅返回 query 实际提供的有效字段，未提供的不写入） */
function parseQueryParams(): Partial<AppParams> {
  const url = new URLSearchParams(window.location.search)
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(QUERY_META) as (keyof AppParams)[]) {
    const raw = url.get(QUERY_META[key].urlKey)
    if (raw === null) continue
    const { serialize } = QUERY_META[key]
    if (serialize === 'json') {
      try { out[key] = JSON.parse(raw) } catch { /* 忽略非法 json */ }
    } else if (serialize === 'number') {
      const num = Number(raw)
      if (num > 0) out[key] = num
    } else {
      out[key] = raw
    }
  }
  return out as Partial<AppParams>
}

/** RN 注入的 window.__NATIVE_PARAMS__ 解析 */
function parseNativeParams(): Partial<AppParams> {
  const p = (window as any).__NATIVE_PARAMS__
  if (!p || typeof p !== 'object') return {}
  const { language, client_type, version, time_zone, inset_top, inset_bottom, _cache } = p
  const cache = _cache || {}
  const out: Record<string, unknown> = {}
  if (language) out.language = language
  if (client_type) out.client_type = client_type
  if (version) out.version = version
  if (time_zone) out.time_zone = time_zone
  if (inset_top) out.inset_top = Number(inset_top)
  if (inset_bottom) out.inset_bottom = Number(inset_bottom)
  if (cache.userInfo?.token) out.token = cache.userInfo.token
  if (cache.cached_unique_id) out.client_id = cache.cached_unique_id
  if (cache.userInfo) out.user_info = cache.userInfo
  return out as Partial<AppParams>
}

/**
 * 应用启动初始化参数：新版 __NATIVE_PARAMS__ 注入优先，旧版 URL query 补缺。
 * 逐字段合并——两者均只取有效值，故 native 未提供的字段自动回退到 query。
 */
export function initAppParams(): void {
  const merged: Partial<AppParams> = { ...parseQueryParams(), ...parseNativeParams() }
  if (!merged.inset_top) merged.inset_top = INSET_TOP_DEFAULT
  if (!merged.inset_bottom) merged.inset_bottom = INSET_BOTTOM_DEFAULT
  setAppParams(merged)
}

/** 清除敏感参数 */
export function clearAppParams(): void {
  setAppParams({ token: null, user_info: null })
}

/** 组件内响应式读取参数 */
export function useAppParams(): AppParams {
  return state
}
